import React, { useEffect, useState } from 'react';
import api from '../api/axiosConfig';
import { apiErrorText } from '../utils/apiErrors';
import './CompaniesPage.css';
import '../styles/rowActions.css';

// Shared CRUD page for simple one-text-field catalogs (Exportadores, Marcas, Tipos de Caja, …).
// `field` is that text column's name in the API ("name" for most, "type" for box types).
// `labels` carries the Spanish wording so each catalog reads naturally.
// `extraFields` adds optional text columns: [{ key, label, placeholder }]. A field with
// `optionsEndpoint` is a required dropdown of another catalog's rows instead (a foreign key):
// { key, label, placeholder, optionsEndpoint, displayKey } — `displayKey` is the API field with
// the chosen row's name, shown in the list. A field with `choices` is an optional dropdown of fixed
// values: { key, label, placeholder, choices: (values) => [[value, label], …], dependsOn } —
// `dependsOn` names another field whose change clears this one when its value no longer fits.
// A field with `checkbox: true` is a yes/no option: { key, label, title } (`title` explains it on hover).
export default function NamedCatalogPage({ endpoint, icon, labels, field = 'name', extraFields = [] }) {
  const [items, setItems] = useState([]);
  const [options, setOptions] = useState({}); // field key -> rows of its optionsEndpoint
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  const [newName, setNewName] = useState('');
  const [newExtra, setNewExtra] = useState({});
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editExtra, setEditExtra] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const selectFields = extraFields.filter((f) => f.optionsEndpoint);
  const trimmed = (values) => Object.fromEntries(
    extraFields.map((f) => {
      if (f.optionsEndpoint) return [f.key, values[f.key] || null];
      if (f.choices) return [f.key, values[f.key] || ''];
      if (f.checkbox) return [f.key, !!values[f.key]];
      return [f.key, (values[f.key] || '').trim()];
    }),
  );
  const fits = (f, values) => !values[f.key] || f.choices(values).some(([v]) => v === values[f.key]);
  // Sets a field, clearing the fields that depend on it when their value no longer fits.
  const setField = (setValues, key, value) => setValues((v) => {
    const next = { ...v, [key]: value };
    extraFields.filter((f) => f.dependsOn === key && !fits(f, next)).forEach((f) => { next[f.key] = ''; });
    return next;
  });
  const choiceLabel = (f, item) => (f.choices(item).find(([v]) => v === item[f.key]) || [])[1];
  const cellText = (f, item) => {
    if (f.checkbox) return item[f.key] ? '✓ Sí' : '—';
    return (f.choices ? choiceLabel(f, item) : item[f.displayKey || f.key]) || '—';
  };
  const missingSelect = (values) => selectFields.some((f) => !values[f.key]);

  useEffect(() => {
    extraFields.filter((f) => f.optionsEndpoint).forEach((f) => {
      api.get(f.optionsEndpoint)
        .then((r) => setOptions((o) => ({ ...o, [f.key]: r.data })))
        .catch(() => setStatus({ type: 'error', text: `No se pudieron cargar las opciones de ${f.label}.` }));
    });
  }, [extraFields]);

  // A text box, the dropdown of a foreign-key field, or a dropdown of fixed choices.
  const fieldInput = (f, values, setValues, className) => {
    if (f.checkbox) {
      return (
        <label key={f.key} className="companies-check" title={f.title}>
          <input
            type="checkbox"
            checked={!!values[f.key]}
            onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.checked }))}
          />
          {f.label}
        </label>
      );
    }
    if (f.choices) {
      const choices = f.choices(values);
      return (
        <select
          key={f.key}
          className={className}
          value={values[f.key] || ''}
          onChange={(e) => setField(setValues, f.key, e.target.value)}
          aria-label={f.label}
          disabled={!choices.length}
        >
          <option value="">{f.placeholder || f.label}</option>
          {choices.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </select>
      );
    }
    return textOrCatalogInput(f, values, setValues, className);
  };
  const textOrCatalogInput = (f, values, setValues, className) => (f.optionsEndpoint ? (
    <select
      key={f.key}
      className={className}
      value={values[f.key] || ''}
      onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value ? Number(e.target.value) : '' }))}
      aria-label={f.label}
    >
      <option value="">{f.placeholder || f.label}</option>
      {(options[f.key] || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
    </select>
  ) : (
    <input
      key={f.key}
      className={className}
      placeholder={f.placeholder || f.label}
      value={values[f.key] || ''}
      onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
    />
  ));

  useEffect(() => {
    setLoading(true);
    api.get(endpoint)
      .then((r) => setItems(r.data))
      .catch(() => setStatus({ type: 'error', text: `No se pudieron cargar ${labels.pluralArticle}.` }))
      .finally(() => setLoading(false));
  }, [endpoint, labels.pluralArticle]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim() || missingSelect(newExtra)) return;
    setCreating(true);
    setStatus(null);
    try {
      const r = await api.post(endpoint, { [field]: newName.trim(), ...trimmed(newExtra) });
      setItems((xs) => [...xs, r.data].sort((a, b) => a[field].localeCompare(b[field])));
      setNewName('');
      setNewExtra({});
    } catch (err) {
      setStatus({ type: 'error', text: apiErrorText(err, `No se pudo crear ${labels.singularArticle}.`) });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditName(item[field]);
    setEditExtra(Object.fromEntries(extraFields.map((f) => [f.key, f.checkbox ? !!item[f.key] : item[f.key] ?? ''])));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditExtra({});
  };

  const handleSaveEdit = async (id) => {
    if (!editName.trim() || missingSelect(editExtra)) return;
    setSavingId(id);
    setStatus(null);
    try {
      const r = await api.patch(`${endpoint}${id}/`, { [field]: editName.trim(), ...trimmed(editExtra) });
      setItems((xs) => xs.map((x) => (x.id === id ? r.data : x)));
      cancelEdit();
    } catch (err) {
      setStatus({ type: 'error', text: apiErrorText(err, 'No se pudieron guardar los cambios.') });
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`¿Eliminar ${labels.singularDemonstrative}?`)) return;
    setDeletingId(id);
    setStatus(null);
    try {
      await api.delete(`${endpoint}${id}/`);
      setItems((xs) => xs.filter((x) => x.id !== id));
    } catch (err) {
      setStatus({ type: 'error', text: apiErrorText(err, `No se pudo eliminar ${labels.singularArticle}.`) });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="companies-root">
      <h1 className="companies-title">{icon} {labels.title}</h1>

      <form className="companies-add-form" onSubmit={handleCreate}>
        <input
          className="companies-add-input"
          placeholder={labels.newPlaceholder}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        {extraFields.map((f) => fieldInput(f, newExtra, setNewExtra, 'companies-add-input'))}
        <button className="companies-add-btn" type="submit" disabled={creating || !newName.trim() || missingSelect(newExtra)}>
          {creating ? 'Agregando…' : `+ ${labels.addButton}`}
        </button>
      </form>

      {status && <div className={`companies-status companies-status-${status.type}`}>{status.text}</div>}

      <div className="companies-card">
        {loading ? (
          <div className="companies-empty">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="companies-empty">{labels.empty}</div>
        ) : (
          <table className="companies-table">
            <thead>
              <tr>
                <th>{labels.column || 'Nombre'}</th>
                {extraFields.map((f) => <th key={f.key} title={f.title}>{f.label}</th>)}
                <th>Creado por</th>
                <th>Creado</th>
                <th>Editado por</th>
                <th>Modificado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    {editingId === item.id ? (
                      <input
                        className="companies-edit-input"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        autoFocus
                      />
                    ) : (
                      item[field]
                    )}
                  </td>
                  {extraFields.map((f) => (
                    <td key={f.key}>
                      {editingId === item.id
                        ? fieldInput(f, editExtra, setEditExtra, 'companies-edit-input')
                        : cellText(f, item)}
                    </td>
                  ))}
                  <td>{item.created_by_username || '—'}</td>
                  <td>{new Date(item.created).toLocaleDateString()}</td>
                  <td>{item.last_edited_by_username || '—'}</td>
                  <td>{new Date(item.modified).toLocaleDateString()}</td>
                  <td>
                    <div className="row-actions">
                      {editingId === item.id ? (
                        <>
                          <button
                            className="row-icon-btn"
                            onClick={() => handleSaveEdit(item.id)}
                            disabled={savingId === item.id}
                            title="Guardar"
                            aria-label="Guardar"
                          >
                            {savingId === item.id ? '…' : '💾'}
                          </button>
                          <button className="row-icon-btn" onClick={cancelEdit} title="Cancelar" aria-label="Cancelar">✖️</button>
                        </>
                      ) : (
                        <>
                          <button className="row-icon-btn" onClick={() => startEdit(item)} title="Editar" aria-label="Editar">✏️</button>
                          <button
                            className="row-icon-btn"
                            onClick={() => handleDelete(item.id)}
                            disabled={deletingId === item.id}
                            title="Eliminar"
                            aria-label="Eliminar"
                          >
                            {deletingId === item.id ? '…' : '🗑️'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
